/**
 * VideoHero component - Full-bleed video hero section
 *
 * A hero section with video background that takes up the full width
 * with no padding, designed for maximum visual impact.
 */

import {
  Box,
  Typography,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  ButtonProps,
} from '@mui/material';
import { ReactNode } from 'react';

export interface VideoHeroAction {
  /** Button label */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Button variant */
  variant?: ButtonProps['variant'];
  /** Button color */
  color?: ButtonProps['color'];
  /** Button size */
  size?: ButtonProps['size'];
  /** Whether button is disabled */
  disabled?: boolean;
  /** Button start icon */
  startIcon?: ReactNode;
  /** Button end icon */
  endIcon?: ReactNode;
}

export interface VideoHeroProps {
  /** Main headline */
  title: string;
  /** Supporting subtitle/description */
  subtitle?: string;
  /** Video source URL */
  videoSrc: string;
  /** Call-to-action buttons */
  actions?: VideoHeroAction[];
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Overlay opacity (0-1) */
  overlayOpacity?: number;
}

/**
 * Full-bleed video hero component
 */
export const VideoHero = ({
  title,
  subtitle,
  videoSrc,
  actions = [],
  align = 'center',
  overlayOpacity = 0.75,
}: VideoHeroProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const alignmentMap = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: '600px', sm: '700px', md: '70vh' },
        maxHeight: '70vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: align,
      }}
    >
      {/* Video Background */}
      <Box
        component="video"
        autoPlay
        muted
        loop
        playsInline
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: '100%',
          minHeight: '100%',
          width: 'auto',
          height: 'auto',
          objectFit: 'cover',
          zIndex: 1,
        }}
      >
        <source src={videoSrc} type="video/mp4" />
      </Box>

      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, rgba(25, 118, 210, ${overlayOpacity}) 0%, rgba(13, 71, 161, ${overlayOpacity}) 100%)`,
          zIndex: 2,
        }}
      />

      {/* Content */}
      <Stack
        direction="column"
        alignItems={alignmentMap[align]}
        spacing={{ xs: 3, md: 4 }}
        sx={{
          position: 'relative',
          zIndex: 3,
          width: '100%',
          maxWidth: '800px',
          px: { xs: 3, sm: 4, md: 6 },
          mx: align === 'center' ? 'auto' : undefined,
          color: 'white',
        }}
      >
        <Typography
          variant={isMobile ? 'h3' : 'h1'}
          component="h1"
          sx={{
            fontWeight: 'bold',
            lineHeight: 1.2,
            maxWidth: '100%',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            component="p"
            sx={{
              opacity: 0.95,
              maxWidth: '600px',
              lineHeight: 1.5,
              textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
            }}
          >
            {subtitle}
          </Typography>
        )}

        {actions.length > 0 && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 3 }}
            alignItems={align === 'center' ? 'center' : 'flex-start'}
            sx={{ width: { xs: '100%', sm: 'auto' }, mt: 2 }}
          >
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || (index === 0 ? 'contained' : 'outlined')}
                color={action.color || 'primary'}
                size={action.size || 'large'}
                onClick={action.onClick}
                disabled={action.disabled ?? false}
                startIcon={action.startIcon}
                endIcon={action.endIcon}
                sx={{
                  minWidth: { xs: '200px', sm: 'auto' },
                  backgroundColor:
                    action.variant === 'contained' || index === 0
                      ? 'white'
                      : 'transparent',
                  color:
                    action.variant === 'contained' || index === 0
                      ? 'primary.main'
                      : 'white',
                  borderColor: 'white',
                  '&:hover': {
                    backgroundColor:
                      action.variant === 'contained' || index === 0
                        ? 'rgba(255, 255, 255, 0.9)'
                        : 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'white',
                  },
                }}
              >
                {action.label}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default VideoHero;
